import { Badge, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { resolveColumnDisplayValue } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  ExportGridButton,
  GridColumn,
  GridColumns,
  GridRelatedRecordsColumn,
  MainGrid,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';

// Mirrors Blazor `GridRelatedRecordsDemo`: a `<MainGrid>` over the account
// table with two `<GridRelatedRecordsColumn>`s over the same one-to-many
// relationship (contact_customer_accounts — the contacts whose company is
// the account):
//
//   - "Contacts" — the default rendering: the contacts' names, sorted and
//     comma-separated, each a link to the contact's demo page.
//   - "Contact emails" — an `itemRenderer` drawing each contact as a badge
//     holding its email address, which the column asks for with `columns`.
//
// Both columns load in one query for the page, not one per row: the server
// merges two columns over the same relationship into one.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.GridColumns.GridRelatedRecordsDemo';

const ALL_ACCOUNTS_VIEW: ViewDefinition = {
  id: '00000000-0000-0000-0000-000000000002',
  tableName: 'account',
  displayName: 'All Accounts',
  columns: [{ columnName: 'name', width: 220 }],
  fetchXml: `<fetch>
    <entity name="account">
      <attribute name="name" />
      <order attribute="name" />
    </entity>
  </fetch>`,
};

export function GridRelatedRecordsSample() {
  const t = useT();
  return (
    <>
      <MessageBar intent="warning" layout="multiline" style={{ gridTemplateRows: 'auto', minHeight: 'auto' }}>
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="account"
        minHeight="400px"
        maxHeight="400px"
        pageSize={10}
        customViewDefinitions={[ALL_ACCOUNTS_VIEW]}
        defaultViewId={ALL_ACCOUNTS_VIEW.id}
        viewIds={[ALL_ACCOUNTS_VIEW.id]}
      >
        <GridColumns>
          <GridColumn columnName="name" width="220px" />

          {/* The contacts' names, each a link to the contact's page. */}
          <GridRelatedRecordsColumn
            relationshipName="contact_customer_accounts"
            displayName={t(`${STRINGS_BASE}.column-contacts`)}
            width="300px"
            // Relative, as the site's own links are, so it resolves against the page's base.
            urlFor={(contact) => `demos/contact?contactId=${contact.id}`}
          />

          {/* Each contact as a badge with its email address — a column the
              related records are read with because `columns` names it. */}
          <GridRelatedRecordsColumn
            relationshipName="contact_customer_accounts"
            displayName={t(`${STRINGS_BASE}.column-contact-emails`)}
            columns={['emailaddress1']}
            itemRenderer={({ record }) => {
              const email = resolveColumnDisplayValue(record, 'emailaddress1');
              return email ? <Badge appearance="tint">{email}</Badge> : null;
            }}
          />
        </GridColumns>

        {/* Exports each related-records column as it shows its records: names in one,
            email addresses in the other. */}
        <ExportGridButton />
      </MainGrid>
    </>
  );
}
